"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSyncExternalStore } from "react";

import { ChevronRightIcon } from "lucide-react";
import { useTranslations } from "next-intl";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { activeHref, isSingleLink, visibleNav, type NavTitle } from "@/config/nav";
import { useCurrentUser } from "@/features/auth/components/current-user-provider";

/** navigation.md rule 12: which sections the user opened or closed, per browser. */
const STORAGE_KEY = "nav-sections";
type Stored = Partial<Record<NavTitle, boolean>>;

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

/** The raw string: a stable snapshot. Failures (private mode, blocked storage) are ignored. */
function readRaw(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function parse(raw: string | null): Stored {
  try {
    return raw ? (JSON.parse(raw) as Stored) : {};
  } catch {
    return {};
  }
}

function write(value: Stored): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // The sections just don't remember
  }
  for (const listener of listeners) listener();
}

/** Reads the nav registry directly: icon components can't be passed from a Server Component. */
export function NavMain() {
  const pathname = usePathname();
  const { permissions } = useCurrentUser();
  const t = useTranslations("nav");
  const active = activeHref(pathname);
  // On the server (and while hydrating) nothing is stored, so both renders agree
  const stored = parse(useSyncExternalStore(subscribe, readRaw, () => null));
  const toggle = (section: NavTitle, open: boolean) => write({ ...stored, [section]: open });

  return (
    <SidebarGroup>
      <SidebarMenu>
        {visibleNav(permissions).map((section) => {
          const [first] = section.entries;
          if (first && isSingleLink(section)) {
            return (
              <SidebarMenuItem key={section.title}>
                <SidebarMenuButton
                  asChild
                  tooltip={t(section.title)}
                  isActive={active === first.href}
                >
                  <Link
                    href={first.href}
                    aria-current={first.href === pathname ? "page" : undefined}
                  >
                    <section.icon aria-hidden />
                    <span>{t(section.title)}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          }
          const containsActive = section.entries.some((entry) => entry.href === active);
          return (
            <Collapsible
              key={section.title}
              asChild
              open={stored[section.title] ?? containsActive}
              onOpenChange={(open) => toggle(section.title, open)}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={t(section.title)}>
                    <section.icon aria-hidden />
                    <span>{t(section.title)}</span>
                    <ChevronRightIcon
                      aria-hidden
                      className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                    />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {section.entries.map((entry) => (
                      <SidebarMenuSubItem key={entry.href}>
                        <SidebarMenuSubButton asChild isActive={entry.href === active}>
                          <Link
                            href={entry.href}
                            aria-current={entry.href === pathname ? "page" : undefined}
                          >
                            {t(entry.title)}
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
